<template>
    <div>
        <!-- Figma UQ "Details" card: "Add Notes" + notes box with the "Write your Notes..." bar (same as Internal Notes) -->
        <p v-if="!hideTitle" class="m-0 mb-[8px] text-[16px] font-roboto font-semibold text-[#333333]">
            Add Notes
        </p>
        <!-- same notes box as S&R (nest_notes): own notes grey, others white; input row at the bottom -->
        <div :class="['flex flex-col border-1 border-[#CCCCCC] rounded-[10px] bg-white p-[5px] font-roboto', innerClass ? '' : 'laptop:min-h-[267px] max-h-[400px]']">
            <div ref="notesContainer"
                :class="['flex-1 overflow-x-hidden overflow-y-auto p-[5px] flex flex-col gap-[10px]', innerClass]">
                <div v-for="(item, index) in jobNotes" :key="index">
                    <div class="relative rounded-[10px] p-[10px] pr-[26px] text-[12px] text-[#333333] break-words"
                        :class="item.is_deletable == 1 ? 'bg-[#EFEFEF]' : 'bg-white border-1 border-[#EFEFEF]'">
                        <!-- who wrote it: small grey caption above the text, like the S&R / RQ notes -->
                        <p v-if="item.sender_name" class="m-0 mb-[2px] text-[11px] text-[#999999]">{{ item.sender_name }}</p>
                        <p class="m-0 leading-[20px] text-[14px] whitespace-pre-line break-words">{{ item.notes }}</p>
                        <button v-if="item.is_deletable == 1" type="button" aria-label="Delete note"
                            class="absolute right-[10px] -bottom-[10px] p-0 leading-none w-[26px] h-[26px] rounded-full flex items-center justify-center border !border-[#EFEFEF] text-[#F67280] !bg-[#fff] hover:text-[#fff] hover:!bg-[#F67280]"
                            @click="deleteNoteBtn(item)">
                            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" class="w-[13px] h-[13px]">
                                <path d="M0 0h24v24H0z" fill="none" />
                                <path fill="currentColor" fill-rule="evenodd"
                                    d="M9.774 5L3.758 3.94l.174-.986a.5.5 0 0 1 .58-.405L18.411 5h.088h-.087l1.855.327a.5.5 0 0 1 .406.58l-.174.984l-2.09-.368l-.8 13.594A2 2 0 0 1 15.615 22H8.386a2 2 0 0 1-1.997-1.883L5.59 6.5h12.69zH5.5zM9 9l.5 9H11l-.4-9zm4.5 0l-.5 9h1.5l.5-9zm-2.646-7.871l3.94.694a.5.5 0 0 1 .405.58l-.174.984l-4.924-.868l.174-.985a.5.5 0 0 1 .58-.405z" />
                            </svg>
                        </button>
                    </div>
                    <p class="m-0 mt-[4px] px-[2px] text-[10px] text-[#999999]">
                        {{ convertNotesTimsStampToDate(item.created_at) }}
                    </p>
                </div>
                <p v-if="!jobNotes?.length" class="m-0 py-4 text-center text-[12px] text-[#999999]">No notes available</p>
            </div>
            <div
                class="flex items-end gap-2 mt-[5px] rounded-[10px] bg-white shadow-[0px_0px_20px_0px_#00000026] pl-[10px] pr-[5px] py-[5px]">
                <textarea ref="notesArea" v-model="notes" rows="1"
                    class="flex-1 min-w-0 resize-none border-0 outline-none bg-transparent py-[5px] min-h-[30px] max-h-[72px] hide-scrollbar leading-[20px] text-[14px] text-[#333333] placeholder:text-[#666666]"
                    placeholder="Write your Notes..." @input="autoResize" />
                <button v-if="selectedNotes != null" type="button" :disabled="txtDisable"
                    class="h-[30px] px-[10px] flex-shrink-0 rounded-[6px] border-0 bg-[#7695FF] text-[13px] text-white disabled:bg-[#EFEFEF] disabled:text-[#999999]"
                    @click="updateNotesBtn">
                    Update Note
                </button>
                <button v-else type="button" :disabled="txtDisable" aria-label="Send note"
                    class="w-[30px] h-[30px] flex-shrink-0 rounded-[6px] bg-[#EFEFEF] border-0 flex items-center justify-center disabled:opacity-50"
                    @click="addNotesBtn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 16 16" aria-hidden="true">
                        <path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd"
                            d="M12.97 2.67a.5.5 0 0 0-.64-.64l-11 4a.5.5 0 0 0-.016.934l4.433 1.773l2.9-3.09l.707.707l-2.98 3.176l1.662 4.156a.5.5 0 0 0 .934-.015z" />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { useToast } from "vue-toastification";
const toast = useToast();

export default {
    components: {},
    props: {
        innerClass: {
            type: String,
            default: '',
        },
        // the card shows its own title (e.g. UQ detail "Internal Notes")
        hideTitle: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            modal: null,
            selectedNotes: null,
            notes: "",
            jobNotes: [],
            txtDisable: true,
        };
    },
    computed: {
        ...mapGetters(["quoteDetail", "notesData", "notesDataResponse"]),
    },

    watch: {
        notesData: function () {
            this.selectedNotes = null;
            this.notes = "";
            this.getNotesData()
        },

        notesDataResponse: function () {
            this.selectedNotes = null;
            this.notes = "";
            this.jobNotes = this.notesDataResponse ?? [];
            this.scrollToBottom()
        },

        notes: function () {
            if (!this.isEmpty(this.notes)) {
                this.txtDisable = false;
            } else {
                this.txtDisable = true;
            }
        },
    },

    mounted() {
        this.getNotesData()
    },

    methods: {
        ...mapActions(["updateNote", "addNote", "deleteNote", "getNotes"]),
        autoResize() {
            const textarea = this.$refs.notesArea;
            textarea.style.height = "auto";
            textarea.style.height = Math.min(textarea.scrollHeight, 72) + "px";
            // 72px = approx 3 lines
        },
        resetTextareaHeight() {
            this.$nextTick(() => {
                const textarea = this.$refs.notesArea;
                if (textarea) {
                    textarea.style.height = "40px"; // 1 line height reset
                }
            });
        },
        getNotesData() {
            this.getNotes({
                quote_id: this.quoteDetail.id,
            });
        },

        convertNotesTimsStampToDate(val) {
            return moment.unix(val).format("MM/DD/YYYY hh:mm A");
        },
        editNoteBtn(data) {
            this.selectedNotes = data;
            this.notes = this.selectedNotes.notes;
        },
        deleteNoteBtn(data) {
            this.$dialog.show({
                title: "Delete Note",
                text: "Are you sure you want to delete this note?",
                // image: "pop-req-img.png",
                // we are passing callback method for our confirm button
                onConfirm: () => {
                    this.deleteNote({
                        id: data.id,
                    });
                },
            });
        },
        addNotesBtn() {
            if (this.isValid()) {
                this.addNote({
                    quote_id: this.quoteDetail.id,
                    notes: this.notes,
                });
            }
            this.notes = "";
            this.resetTextareaHeight();
        },
        updateNotesBtn() {
            if (this.isValid()) {
                this.updateNote({
                    id: this.selectedNotes.id,
                    notes: this.notes,
                });
            }
            this.notes = "";
            this.resetTextareaHeight();
        },
        isValid() {
            let res = true;
            if (this.isEmpty(this.notes)) {
                toast.error("Please enter note");
                res = false;
            }
            return res;
        },
        isEmpty(value) {
            return (
                value == null ||
                value.trim().length === 0 ||
                (value.constructor === Object && Object.keys(value).length === 0)
            );
        },
        scrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.notesContainer;
                if (el) {
                    el.scrollTop = el.scrollHeight;
                }
            });
        }
    },
};
</script>
<style>
/* Scrollbar hide for Chrome, Safari */
.hide-scrollbar::-webkit-scrollbar {
    display: none;
}

/* Scrollbar hide for Firefox */
.hide-scrollbar {
    scrollbar-width: none;
}

/* Scrollbar hide for IE/Edge */
.hide-scrollbar {
    -ms-overflow-style: none;
}
</style>